import { useEffect } from "react";

/** Attribute set on an activated target element.  Exposed for use as a CSS styling hook
 *  (e.g. dim/outline a target until it has been activated). */
export const SCROLL_GATE_ENABLED_ATTRIBUTE = "data-scroll-gate-enabled";

/**
 * Gates wheel-hijacking descendants (pan/zoom surfaces, embedded maps, zoomable charts, ...)
 * behind a click-to-activate interaction, Google-Maps-style.
 *
 * Components which zoom on wheel events swallow page scrolling as soon as the pointer passes over
 * them, trapping the user.  This hook suppresses wheel events over any descendant matching
 * `targetSelector` until the user clicks it; moving the pointer off the target re-arms the gate.
 *
 * Mechanism: a capture-phase wheel listener on the container calls `stopPropagation()` (never
 * `preventDefault()`) for events over non-activated targets, so the target's own wheel handlers
 * never fire and the browser performs its default scroll.  Works regardless of whether the target
 * uses React synthetic events or native listeners, since both sit later in the propagation path.
 *
 * @param containerRef Ref to the element the (delegated) listeners are attached to.  Must be
 *                     rendered unconditionally alongside the targets it contains.
 * @param targetSelector CSS selector identifying the wheel-hijacking elements to gate,
 *                       e.g. `[data-streamdown="mermaid-block"]`.
 */
export function useScrollGate(containerRef: React.RefObject<HTMLElement>, targetSelector: string) {
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;

        const getTarget = (target: EventTarget) => (target as HTMLElement)?.closest?.(targetSelector);

        const onWheel = (e: WheelEvent) => {
            const target = getTarget(e.target);
            if (target && !target.hasAttribute(SCROLL_GATE_ENABLED_ATTRIBUTE)) e.stopPropagation();
        };
        const onClick = (e: MouseEvent) => getTarget(e.target)?.setAttribute(SCROLL_GATE_ENABLED_ATTRIBUTE, "true");
        const onMouseOut = (e: MouseEvent) => {
            const target = getTarget(e.target);
            if (target && !target.contains(e.relatedTarget as Node)) target.removeAttribute(SCROLL_GATE_ENABLED_ATTRIBUTE);
        };

        container.addEventListener("wheel", onWheel, { capture: true, passive: true });
        container.addEventListener("click", onClick);
        container.addEventListener("mouseout", onMouseOut);
        return () => {
            container.removeEventListener("wheel", onWheel, { capture: true });
            container.removeEventListener("click", onClick);
            container.removeEventListener("mouseout", onMouseOut);
        };
    }, [targetSelector]);
}
